import React from "react";
import { Table, Tag, Button, Space, Popconfirm, Switch, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { IPromotionRule } from "../hooks/usePromotions";

interface TablePromotionsProps {
  promotions: IPromotionRule[];
  loading: boolean;
  onEdit: (promotion: IPromotionRule) => void;
  onView: (promotion: IPromotionRule) => void;
  onDelete: (id: number) => void;
  onToggleStatus: (id: number, status: "active" | "inactive") => void;
}

const PROMO_TYPE_LABELS: Record<string, string> = {
  "2x1": "2x1 (Lleva 2, paga 1)",
  "3x2": "3x2 (Lleva 3, paga 2)",
  custom: "Personalizada",
};

const DAY_LABELS: Record<string, string> = {
  monday: "Lun",
  tuesday: "Mar",
  wednesday: "Mié",
  thursday: "Jue",
  friday: "Vie",
  saturday: "Sáb",
  sunday: "Dom",
};

export const TablePromotions: React.FC<TablePromotionsProps> = ({
  promotions,
  loading,
  onEdit,
  onView,
  onDelete,
  onToggleStatus,
}) => {
  const getDaysActive = (promotion: IPromotionRule) => {
    const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    return days
      .filter((day) => promotion[day as keyof IPromotionRule] === "true")
      .map((day) => DAY_LABELS[day])
      .join(", ");
  };

  const getCategoriesSummary = (promotion: IPromotionRule) => {
    if (!promotion.rule_categories?.length) return "Sin categorías";
    
    const primary = promotion.rule_categories.filter((c) => c.role === "primary");
    const secondary = promotion.rule_categories.filter((c) => c.role === "secondary");
    
    const parts: string[] = [];
    if (primary.length > 0) {
      parts.push(`${primary.map((c) => `${c.required_quantity} de ${c.category?.name || "?"}`).join(", ")}`);
    }
    if (secondary.length > 0) {
      parts.push(`+ ${secondary.map((c) => `${c.required_quantity} de ${c.category?.name || "?"}`).join(" ó ")}`);
    }
    
    return parts.join(" ");
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: IPromotionRule) => (
        <div>
          <strong>{name}</strong>
          {record.description && (
            <div style={{ fontSize: 12, color: "#666" }}>{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "promo_type",
      key: "promo_type",
      render: (type: string) => (
        <Tag color={type === "2x1" ? "blue" : type === "3x2" ? "purple" : "orange"}>
          {PROMO_TYPE_LABELS[type] || type}
        </Tag>
      ),
    },
    {
      title: "Configuración",
      key: "categories",
      render: (_: any, record: IPromotionRule) => (
        <Tooltip title={getCategoriesSummary(record)}>
          <span style={{ cursor: "pointer" }}>
            {getCategoriesSummary(record).substring(0, 40)}
            {getCategoriesSummary(record).length > 40 ? "..." : ""}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Días",
      key: "days",
      render: (_: any, record: IPromotionRule) => (
        <span style={{ fontSize: 12 }}>{getDaysActive(record) || "Ninguno"}</span>
      ),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: IPromotionRule) => (
        <Switch
          checked={status === "active"}
          onChange={(checked) =>
            onToggleStatus(record.id, checked ? "active" : "inactive")
          }
          checkedChildren="Activo"
          unCheckedChildren="Inactivo"
        />
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: IPromotionRule) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar esta promoción?"
            onConfirm={() => onDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Tooltip title="Eliminar">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      dataSource={promotions}
      columns={columns}
      loading={loading}
      rowKey="id"
      pagination={{ pageSize: 10 }}
    />
  );
};
